import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { Platform, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { type Edges, SafeAreaView } from 'react-native-safe-area-context';

import {
  BodyText,
  Button,
  type ContentfulImageTransformOptions,
  FallbackImage,
  TitleText,
  LabelText,
  theme,
  Icon,
  TextLink,
  useResponsive,
} from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const LoyaltyFeaturedRewardHero = (props: Props) => {
  const { content, title, variant, onContinue, onOrderToRedeem, onShowTerms } = props;

  const { match } = useResponsive();

  // ─── Styles ────────────────────────────────────────────────────────────────

  const landscapeContentStyles = [
    styles.landscapeContent,
    match([styles.landscapeContentXS, styles.landscapeContentXS, styles.landscapeContentMD]),
  ];

  // ─── Vertical Variant ──────────────────────────────────────────────────────

  if (variant === 'vertical') {
    return (
      <SafeAreaView style={styles.verticalContainer} edges={VERTICAL_SAFE_AREA_EDGES}>
        <ScrollView
          style={styles.verticalScroll}
          contentContainerStyle={styles.verticalScrollContent}
          bounces={false}
        >
          <View style={styles.verticalContent}>
            <FeaturedRewardContent
              content={content}
              title={title}
              variant={variant}
              onShowTerms={onShowTerms}
            />
          </View>

          <FeaturedRewardImage
            imageUrl={content.imageUrl}
            imageAlt={content.imageAlt}
            tag={content.tag}
            variant={variant}
          />
        </ScrollView>

        <View style={styles.verticalActions}>
          <Button size="large" palette="cream" onPress={onContinue}>
            <FormattedMessage {...messages.continue} />
          </Button>

          <Button size="large" palette="sweetCorn" onPress={onOrderToRedeem}>
            {content.orderCtaLabel}
          </Button>
        </View>
      </SafeAreaView>
    );
  }

  // ─── Landscape Variant ─────────────────────────────────────────────────────

  return (
    <View style={styles.landscapeContainer}>
      <View style={styles.landscapeContentColumn}>
        <View style={landscapeContentStyles}>
          <FeaturedRewardContent
            content={content}
            title={title}
            variant={variant}
            onShowTerms={onShowTerms}
          />

          <View style={styles.landscapeActions}>
            <Button size="large" palette="whiteFill" onPress={onOrderToRedeem}>
              {content.orderCtaLabel}
            </Button>

            <TouchableOpacity role="button" onPress={onShowTerms}>
              <BodyText sizeMatch={['16']} style={styles.body} underline>
                {content.detailsLinkLabel}
              </BodyText>
            </TouchableOpacity>
          </View>
        </View>
      </View>

      <FeaturedRewardImage
        imageUrl={content.imageUrl}
        imageAlt={content.imageAlt}
        tag={content.tag}
        variant={variant}
      />
    </View>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const FeaturedRewardContent = (props: FeaturedRewardContentProps) => {
  const { content, title, variant, onShowTerms } = props;

  return (
    <View>
      <View style={styles.iconContainer}>
        <Icon name="IconSgRewardWithText2" color={theme.colors.SWEET_CORN} width={150} />
      </View>

      <TitleText style={styles.title} sizeMatch={['56', '48', '56']} maxFontSizeMultiplier={1}>
        {title}
      </TitleText>

      {content.description || variant === 'vertical' ? (
        <View style={styles.termsSummary}>
          {content.description ? (
            <BodyText style={styles.body} sizeMatch={['16']}>
              {content.description}
            </BodyText>
          ) : undefined}

          {variant === 'vertical' ? (
            <TextLink palette="white" sizeMatch={['16']} onPress={onShowTerms}>
              {content.detailsLinkLabel}
            </TextLink>
          ) : undefined}
        </View>
      ) : undefined}
    </View>
  );
};

const FeaturedRewardImage = (props: FeaturedRewardImageProps) => {
  const { imageUrl, imageAlt, tag, variant } = props;

  const { match } = useResponsive();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const contentfulOptions = match([CONTENTFUL_OPTIONS_XS, CONTENTFUL_OPTIONS_SM]);

  // ─── Styles ────────────────────────────────────────────────────────────────

  const imageLabelStyles = [
    styles.imageLabel,
    variant === 'vertical' ? styles.imageLabelVertical : undefined,
  ];

  return (
    <View style={styles.imageContainer}>
      <FallbackImage
        contentFit="cover"
        style={styles.image}
        baseUrl={imageUrl}
        defaultImage={FeaturedRewardFallbackImage}
        contentfulOptions={contentfulOptions}
        alt={imageAlt}
      />

      {tag ? (
        <View style={imageLabelStyles}>
          <LabelText sizeMatch={['12']}>{tag}</LabelText>
        </View>
      ) : undefined}
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  continue: {
    defaultMessage: 'Continue',
    description: 'Loyalty | Featured Reward Hero | Continue CTA',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const FeaturedRewardFallbackImage = require('./assets/feature-reward-fallback.webp');

const VERTICAL_SAFE_AREA_EDGES: Edges = Platform.select({
  android: ['bottom'],
  default: [],
});

const IMAGE_HEIGHT_XS = 300;
const IMAGE_WIDTH_SM = 1000;

const SHARED_CONTENTFUL_OPTIONS: Partial<ContentfulImageTransformOptions> = {
  fm: 'webp',
  fit: 'fill',
  q: 70,
};

const CONTENTFUL_OPTIONS_XS: Partial<ContentfulImageTransformOptions> = {
  ...SHARED_CONTENTFUL_OPTIONS,
  h: IMAGE_HEIGHT_XS,
};

const CONTENTFUL_OPTIONS_SM: Partial<ContentfulImageTransformOptions> = {
  ...SHARED_CONTENTFUL_OPTIONS,
  w: IMAGE_WIDTH_SM,
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  verticalContainer: {
    flex: 1,
    backgroundColor: theme.colors.KALE,
  },
  verticalScroll: {
    flex: 1,
  },
  verticalScrollContent: {
    flexGrow: 1,
  },
  verticalContent: {
    justifyContent: 'center',
    paddingHorizontal: theme.spacing['8'],
    paddingTop: theme.spacing['24'],
    paddingBottom: theme.spacing['8'],
  },
  verticalActions: {
    gap: theme.spacing['3'],
    paddingHorizontal: theme.spacing['8'],
    paddingVertical: theme.spacing['6'],
  },
  landscapeContainer: {
    minHeight: 600,
    flexDirection: 'row',
    backgroundColor: theme.colors.KALE,
    borderBottomColor: theme.colors.FOREST,
    borderBottomWidth: 1,
  },
  landscapeContent: {
    flex: 1,
    width: '100%',
    maxWidth: 760,
    justifyContent: 'center',
    gap: theme.spacing['6'],
  },
  landscapeContentColumn: {
    flex: 1,
    alignItems: 'flex-end',
  },
  landscapeContentXS: {
    paddingHorizontal: theme.spacing['6'],
    paddingVertical: theme.spacing['15'],
  },
  landscapeContentMD: {
    paddingHorizontal: theme.spacing['16'],
    paddingVertical: theme.spacing['20'],
  },
  landscapeActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['4'],
  },

  iconContainer: {
    marginBottom: theme.spacing['4'],
  },
  title: {
    color: theme.colors.WHITE,
  },
  termsSummary: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['1'],
  },
  body: {
    color: theme.colors.WHITE,
  },
  imageContainer: {
    flex: 1,
    minHeight: 100,
  },
  image: {
    width: '100%',
    height: '100%',
    backgroundColor: theme.colors.FOREST,
  },
  imageLabel: {
    position: 'absolute',
    top: 120,
    left: 0,
    backgroundColor: theme.colors.SWEET_CORN,
    borderTopRightRadius: theme.radius.small,
    borderBottomRightRadius: theme.radius.small,
    paddingVertical: theme.spacing['1'],
    paddingHorizontal: theme.spacing['2'],
  },
  imageLabelVertical: {
    top: 30,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  content: Content;
  title: string;
  variant: 'landscape' | 'vertical';
  onContinue?: () => void;
  onOrderToRedeem: () => void;
  onShowTerms: () => void;
};

type FeaturedRewardContentProps = Pick<Props, 'content' | 'title' | 'variant' | 'onShowTerms'>;

type FeaturedRewardImageProps = Pick<Props, 'variant'> &
  Pick<Content, 'imageUrl' | 'imageAlt' | 'tag'>;

type Content = {
  imageUrl?: string;
  imageAlt?: string;
  description?: string;
  tag?: string;
  orderCtaLabel: string;
  detailsLinkLabel: string;
};
